import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";

/**
 * /application?token=… — "Keep it open" / "Close my application" from the email
 * sent to applicants whose application has gone quiet.
 *
 * Pressed by the student or a parent; neither is signed in, and this page never
 * signs anyone in. The token records one answer against one application and
 * nothing else.
 *
 * ?choice=keep|close in the link presses the button on arrival, so the email's
 * two buttons are genuinely one click. The page then shows what was recorded
 * and offers the way back, because a parent misclicking in an inbox should not
 * be stuck with it.
 *
 * Plain fetch rather than functions.invoke, so the function's own sentences
 * reach the page.
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL ?? "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY ?? "";

async function call(
  body: Record<string, unknown>,
): Promise<{ ok: boolean; data: any }> {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/functions/v1/application-choice`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(body),
      },
    );
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && data?.ok !== false, data };
  } catch {
    return {
      ok: false,
      data: {
        error:
          "We could not reach the server. Check your connection and try again.",
      },
    };
  }
}

export default function ApplicationChoicePage() {
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get("token") ?? "",
    [],
  );
  const urlChoice = useMemo(() => {
    const c = new URLSearchParams(window.location.search).get("choice");
    return c === "keep" || c === "close" ? c : null;
  }, []);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | string>(null);
  const [error, setError] = useState<string | null>(null);
  const [first, setFirst] = useState("");
  const [choice, setChoice] = useState<string | null>(null);
  const [gone, setGone] = useState<string | null>(null);

  const send = useCallback(
    async (action: string) => {
      setBusy(action);
      setError(null);
      const { ok, data } = await call({ action, token });
      setBusy(null);
      if (!ok) {
        setError(data?.error ?? "Something went wrong.");
        return;
      }
      if (data.gone) {
        setGone(data.message);
        return;
      }
      setFirst(data.student_first ?? "");
      setChoice(data.choice ?? null);
    },
    [token],
  );

  useEffect(() => {
    (async () => {
      if (!token) {
        setError("This link is missing its code. Please use the button in the email.");
        setLoading(false);
        return;
      }
      // A link with ?choice= presses that button on arrival: one click, as promised.
      await send(urlChoice ?? "load");
      setLoading(false);
    })();
  }, [token, urlChoice, send]);

  const shell = (children: React.ReactNode) => (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        bgcolor: "background.default",
      }}
    >
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 560, width: "100%" }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ fontWeight: 700, letterSpacing: 1 }}
        >
          Ardent Research
        </Typography>
        {children}
      </Paper>
    </Box>
  );

  if (loading) {
    return shell(
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
        <CircularProgress size={20} />
        <Typography color="text.secondary">One moment…</Typography>
      </Box>,
    );
  }

  if (gone) {
    return shell(
      <Typography sx={{ mt: 2, lineHeight: 1.7 }}>{gone}</Typography>,
    );
  }

  const hi = first ? `${first}, ` : "";

  return shell(
    <>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {choice === "keep" && (
        <>
          <Typography variant="h6" sx={{ fontWeight: 800, mt: 1.5, mb: 1 }}>
            {hi}your application stays open.
          </Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
            Noted — you can submit to the One-on-One program whenever you are
            ready, and your application will be waiting. Dr. Li will be in touch
            about how the schedule would work for you.
          </Typography>
          <Button
            size="small"
            disabled={busy !== null}
            onClick={() => send("close")}
          >
            Actually, close my application
          </Button>
        </>
      )}

      {choice === "close" && (
        <>
          <Typography variant="h6" sx={{ fontWeight: 800, mt: 1.5, mb: 1 }}>
            {hi}your application is closed.
          </Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
            Thank you for telling us — that is genuinely useful. You are welcome
            back whenever the timing is better.
          </Typography>
          <Button
            size="small"
            disabled={busy !== null}
            onClick={() => send("reopen")}
          >
            Changed your mind? Reopen it
          </Button>
        </>
      )}

      {choice === null && !error && (
        <>
          <Typography variant="h6" sx={{ fontWeight: 800, mt: 1.5, mb: 1 }}>
            {hi}what would you like to do?
          </Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.7, mb: 2.5 }}>
            The Fall 2026 cohort has closed. One-on-One Research starts at any
            time of year — same program, same tuition, on your own schedule.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              disabled={busy !== null}
              onClick={() => send("keep")}
            >
              {busy === "keep" ? "Saving…" : "Keep it open"}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              disabled={busy !== null}
              onClick={() => send("close")}
            >
              {busy === "close" ? "Saving…" : "Close my application"}
            </Button>
          </Box>
        </>
      )}
    </>,
  );
}
